import colors from "@assets/colors";
import { nanoid } from "@reduxjs/toolkit";
import { AuthContext } from "@src/AuthProvider";
import { useAppDispatch, useAppSelector } from "@src/ducks/ducksHook";
import { selectedViewDocument, selectedDraftDocument, signsecureActions } from "@src/ducks/slices/signsecure.slice";
import { selectSubscriptionSimultaneousUpload, selectIsFreePlan } from "@src/ducks/slices/user.slice";
import _ from "lodash";
import React, { createContext, useCallback, useState, Dispatch, SetStateAction, useEffect } from "react";
import { FileValue, ProviderProps } from "./types";
import AddEncrpytPassword from "../modals/Index";
import AddSuccessful from "../modals/password-success";
import { useWindowDimensions } from "react-native";
import { DrawerContext } from "@src/screens/drawer-screens/drawer-provider";
import { chooseFiles, getUniqueFilename } from "@src/utils/transform-helper";
import { selectedAuthAccessToken } from "@src/ducks/slices/auth.slice";
import { useFetchGet } from "@src/utils/api/hooks";

const parties = [{
  trackId: nanoid(),
  name: "",
  email: "",
  designation: "",
  role: "Sign Only: Sign the document.",
  fill: colors.colorPrimary2,
  ref: React.createRef(),
}];

export interface ContextValue {
  isXSDevice: boolean;
  isSmallerDevice: boolean;
  folderId: string;
  fileDocuments: any[];
  files: FileValue[];
  shareable: boolean;
  encyptedPassword: boolean;
  checkBoxTick: boolean;
  enableGeneratePassword: boolean;
  addSuccess: boolean;
  foldername: string;
  generatePassword: string;
  pdfpasswords: string;
  exceed100Mb: boolean;
  setGeneratePassword:  Dispatch<SetStateAction<string>>;
  enableApplyToAllParties: boolean;
  pdfpassword: string;
  fileActiveIndex: number;
  onSetShareable: () => void;
  onSetPdfEncryptPassword: (value: any) => void;
  onResetFileDocuments: () => void;
  onRemoveAllFiles: () => void;
  onRemoveFile: (index: any) => void;
  onSelectFile: (value: any) => void;
  onSetFolderName: (value: string) => void;
  onApplyToAllPrivacyType: (value: string) => void;
  onApplyPrivacyTypeByTabIndex: (value: string, tabIndex: number) => void;
  onUpdateFile: (index: number, key: string, value: any) => void;
  onApplyToAllParties: () => void;
  copyPartiesToAll: (currentIndex: number) => void;
  setGeneratePasswordTick: Dispatch<SetStateAction<boolean>>;
  setPDFPassword: Dispatch<SetStateAction<string>>;
  setPasswordEncrpyt: Dispatch<SetStateAction<boolean>>;
  setSuccessful: Dispatch<SetStateAction<boolean>>;
  setCheckBoxTick: Dispatch<SetStateAction<boolean>>;
  setFileActiveIndex:  Dispatch<SetStateAction<number>>;
}

const DocumentContext = createContext<ContextValue>({} as ContextValue);

const DocumentProvider = ({ children }: ProviderProps) => {
  const dispatch = useAppDispatch();
  const { height } = useWindowDimensions();
  const accessToken = useAppSelector(selectedAuthAccessToken);
  const { formSheet } = React.useContext(DrawerContext);
  const [files, setFiles] = useState<any>([]);
  const [folderId, setFolderId] = useState<string>("");
  const document: any = useAppSelector(selectedViewDocument);
  const [generatePassword, setGeneratePassword] = React.useState<string>("");
  const [pdfpasswords, setPDFPassword] = React.useState<string>("");
  const { onErrorMessage } = React.useContext(AuthContext);
  const [foldername, setFoldername] = useState<string>("");
  const [shareable, setShareable] = useState<boolean>(false);
  const [encyptedPassword, setPasswordEncrpyt] = useState<boolean>(false);
  const [checkBoxTick, setCheckBoxTick] = useState<boolean>(false);
  const [enableGeneratePassword, setGeneratePasswordTick] = useState<boolean>(false);
  const [pdfpassword, setPdfpassword] = useState<string>("");
  const draftDocument = useAppSelector(selectedDraftDocument);
  const [enableApplyToAllParties, setEnableIamSignatory] = React.useState(false);
  const maxSimultaneousUpload = useAppSelector(selectSubscriptionSimultaneousUpload);
  const [ addSuccess, setSuccessful] = React.useState(false);
  const [exceed100Mb, setExceed100Mb] = React.useState(false);
  const [fileActiveIndex ,setFileActiveIndex]= React.useState<number>(0);
  const [fileDocuments, setFileDocuments] = React.useState<any[]>([]);
  const { loading, data, runRequest } = useFetchGet();
  const isFreePlan = useAppSelector(selectIsFreePlan);

  const onResetFileDocuments = React.useCallback(() => {
    setFileDocuments([]);
    onVerifyUpload();
  }, []);

  const onVerifyUpload = React.useCallback(() => {
    runRequest("/directory/verify-upload-ability");
  }, []);
  

  React.useEffect(() => {
    if (!_.isEmpty(document)) {
      setFileDocuments((prevFileDocuments: any[]) => {
        const exists = prevFileDocuments.some(fd => fd.id === document.id);
    
        const mergeSignatures = (existing: any[][], incoming: any[][]): any[][] => {
          const maxLength = Math.max(existing?.length || 0, incoming?.length || 0);
          const merged = [];
    
          for (let i = 0; i < maxLength; i++) {
            const existingLayer = existing?.[i] || [];
            const incomingLayer = incoming?.[i] || [];
    
            // Merge by field `id`, preferring existing if it has a signature
            const mergedLayer = _.unionBy(
              [...existingLayer, ...incomingLayer],
              "id"
            ).map(sig => {
              const existingSig = existingLayer.find(s => s.id === sig.id);
              const incomingSig = incomingLayer.find(s => s.id === sig.id);
    
              return {
                ...incomingSig,
                ...existingSig, // preserve existing signature values
                signature: existingSig?.signature || incomingSig?.signature,
                signatureType: existingSig?.signatureType || incomingSig?.signatureType,
                signatureId: existingSig?.signatureId || incomingSig?.signatureId,
              };
            });
    
            merged.push(mergedLayer);
          }
    
          return merged;
        };

        const mergeSignature = (
          existing?: any,
          incoming?: any
        ): any | undefined => {
          if (!existing && !incoming) return undefined;
        
          return {
            ...incoming,
            ...existing, // existing takes priority
            signature: existing?.signature || incoming?.signature,
            signatureType: existing?.signatureType || incoming?.signatureType,
            signatureId: existing?.signatureId || incoming?.signatureId,
            signatureParams: existing?.signatureParams || incoming?.signatureParams,
          };
        };
    
        if (exists) {
          return prevFileDocuments.map(fd =>
            fd.id === document.id
              ? {
                ...fd,
                document: {
                  ...fd.document,
                  signature: mergeSignature(
                    document?.signature,
                    fd.document?.signature,
                  ),
                  workflow: {
                    ...fd.document.workflow,
                    signatures: mergeSignatures(
                      document?.workflow?.signatures,
                      fd.document?.workflow?.signatures,
                    ),
                  },
                },
              }
              : fd
          );
        } else {
          return [...prevFileDocuments, { id: document.id, document }];
        }
      });
    }
  }, [document]);

  const onSetShareable = useCallback(() => {
    setShareable(!shareable);
  }, [shareable]);

  const onSetPdfEncryptPassword = useCallback((value: any) => {
   
    setPdfpassword(value);
  }, [pdfpassword]);

  const onApplyToAllPrivacyType = useCallback((privacyType: string) => {
    setFiles((prevData: any) =>
      prevData.map((item: FileValue) => {
        const newItem = {...item, privacyType};

        return newItem;
      })
    );
  }, [files]);

  const onApplyPrivacyTypeByTabIndex = useCallback((privacyType: string, tabIndex: number) => {
    setFiles((prevData: any) =>
      prevData.map((item: FileValue, index: number) => {
        if(index !== tabIndex) return item; // Only apply to the specified tab index
        const newItem = {...item, privacyType};
        
        return newItem;
      })
    );
  }, [files]);

  const onSetFolderName = useCallback(setFoldername, [foldername]);

  const onApplyToAllParties = useCallback(() => {
    setEnableIamSignatory(!enableApplyToAllParties);
  }, [enableApplyToAllParties]);

  const copyPartiesToAll = useCallback((currentIndex: number) => {
    const firstparties = files[currentIndex].parties;
    const emails = firstparties.map((party: any) => party.email);
    setFiles((prevData: any) =>
      prevData.map((item: FileValue) => {
        const newItem = {...item};

        newItem.parties = firstparties;
        if(!_.isEmpty(newItem.signatures)){
          newItem.signatures = newItem.signatures.map((item: any ) => {
            const layerFields = item.layerFields.reduce((result: any[], layer: any) => {
              if(layer.email && !emails.includes(layer.email)){
                // do nothing
              } else{
                result.push(layer);
              }
    
              return result;
            },[]);
    
            return {...item, layerFields};
          });
        }

        return newItem;
      })
    );
  }, [files]);

  const onRemoveAllFiles = useCallback(() => {
    setFiles([]);
    setFolderId("");
    setFoldername("");
  }, []);

  const onRemoveFile = useCallback((item: any) => {
    const updatedData = files.filter((i: any) => !_.isEqual(i, item));
    setFiles(updatedData);
    if(updatedData.length > 0){
      onSetFolderName(updatedData[0].name);
    }
  }, [files]);

  const onSelectFile = useCallback((value: any[]) => {
    if (isFreePlan) {
      if (value.length > maxSimultaneousUpload) {
        const fileLabel = maxSimultaneousUpload > 1 ? "files" : "file";
        const message = `Limit upload to ${maxSimultaneousUpload} ${fileLabel} only.`;
        
        onErrorMessage(message);
        return;
      }
      
      const totalBytes = value.reduce((sum, item) => {
        return sum + (item?.file?.size || 0);
      }, 0);

      const totalMBFileUploaded = (totalBytes / (1024 * 1024)).toFixed(2);

      const responseObject = data?.data;
      if (responseObject) {
        const total = responseObject.usedStorage + Number(totalMBFileUploaded)
        // alert(`${responseObject.usedStorage} + ${Number(totalMBFileUploaded)} = ${Math.round(total)}`)
        if (Math.round(total) >= responseObject.totalStorage) {
          setExceed100Mb(true);
          return
        }
      }
    }

    let fileName = "";
    if (files.length + value.length > maxSimultaneousUpload) {
      const fileLabel = maxSimultaneousUpload > 1 ? "files" : "file";
      const message = `Limit upload to ${maxSimultaneousUpload} ${fileLabel} only.`;
      onErrorMessage(message);
      return;
    }
    const addedFiles = value.map((item: any) => {
      fileName = item.name;
      if(files.length >= 1){
        fileName  = getUniqueFilename(item.name,files);
      }
      return {
        size: item.size,
        name: fileName,
        path: item.file,
        subject: `Please sign ${item.name}`,
        type: item.mimeType,
        lastModified: item.lastModified,
        lastModifiedDate: item.lastModifiedDate,
        uri: item.uri,
        parties,
        trackId: nanoid(), 
        ref: React.createRef(), 
        qrposition: "Bottom right",
        privacyType: "CONFIDENTIAL",
        password: ""
      };
    });
    const newfiles = files.concat(addedFiles);

    setFiles(newfiles);
    if(newfiles.length > 0){
      onSetFolderName(newfiles[0].name);
    }
  }, [data, files, maxSimultaneousUpload]);

  const onUpdateFile = useCallback(
    (index: number, key: string, value: any) => {
      setFiles((prevData: any) =>
        prevData.map((item: FileValue, i: number) =>
          i === index ? { ...item, [key]: value } : item
        )
      );
    },
    [files]
  );

  useEffect(() => {
    if (formSheet) {
      const loadFormSheetFile = async (formSheet: any) => {
        const addedFiles: any = await chooseFiles([formSheet], accessToken);
        console.log(addedFiles);
        onSelectFile(addedFiles);
      };
      loadFormSheetFile(formSheet);
    }
  }, [formSheet]);

  const value = {
    isXSDevice: height < 730,
    isSmallerDevice: height < 785,
    fileDocuments,
    files,
    shareable,
    foldername,
    encyptedPassword,
    addSuccess,
    folderId,
    enableApplyToAllParties,
    pdfpassword,
    checkBoxTick,
    exceed100Mb,
    onSetShareable,
    onSetFolderName,
    onResetFileDocuments,
    onRemoveAllFiles,
    onRemoveFile,
    onSelectFile,
    onUpdateFile,
    onApplyToAllParties,
    copyPartiesToAll,
    onApplyToAllPrivacyType,
    onApplyPrivacyTypeByTabIndex,
    setPasswordEncrpyt,
    setSuccessful,
    onSetPdfEncryptPassword,
    setCheckBoxTick,
    enableGeneratePassword, setGeneratePasswordTick,
    generatePassword, setGeneratePassword,
    pdfpasswords, setPDFPassword,
    fileActiveIndex, setFileActiveIndex
  };

  React.useEffect(() => {
    if(!_.isEmpty(draftDocument)){
      const clone = _.cloneDeep(draftDocument.files);
      setFiles(clone);
      setFolderId(draftDocument.folderId);
      setFoldername(draftDocument.folderName);
      setShareable(draftDocument.isShareToTeam);
      dispatch(signsecureActions.saveDraftDocument({}));
    }
  }, [draftDocument]);

  return (
    <DocumentContext.Provider value={value}>
      {children}
      <AddEncrpytPassword 
        visible={encyptedPassword}
      />
      <AddSuccessful 
        visible={addSuccess}
      />
    </DocumentContext.Provider>
  );
};

export { DocumentContext, DocumentProvider };

